import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  Divider,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { useStores } from "../../../../use-stores";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";
import CustomerHeading from "../../../components/projects/projectDetails/CustomerHeading";
import AddIcon from "@mui/icons-material/Add";
import CreateBulkIcon from "../../../../assets/CreateBulk2Icon.png";
import PermissionBlockLabel from "../../../components/common/pop-up/PermissionBlockLabel ";
import AppButton from "../../../components/common/buttons/AppButton";
import CustomTextField from "../../../components/common/textFields/TextField";
import PackagingOpsTable from "../../../components/projects/packaging-ops/PackagingOpsTable";
import SelectProductPopup from "./SelectProductPopup";
import EditPackPopup from "./EditPackPopup";
import { useNotification } from "../../../../utils/NotificationsContext";
import {
  ProductData,
  PackagingSwagpack,
  SwagpackProduct,
  PackToDelete,
} from "../../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import ConfirmationPopup from "../../../components/common/pop-up/ConfirmationPopup";
import CreateInventoryPopup from "./CreateInventoryPopup";
import ImageAPI from "../../../../api/ImageAPI";
import CustomTypography from "../../../components/common/form/CustomTypography";
import ImageUpload from "../../../components/common/upload/ImageUpload";
import ImageCarousel from "../../../components/common/carousel/ImageCarousel";
import CloseIcon from "@mui/icons-material/Close";
import RemovePackPopup from "./RemovePackPopup";
import CustomToggleSwitch from "../../../components/common/switch/Switch";
import { formatDate } from "../../../components/common/functions/formatDate";

const PackagingOps = () => {
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  const { userStore, projectPackagingOpsStore, projectManagementStore } =
    useStores();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const projectId = Number(params?.["project-id"]);
  const inventory_id =
    projectPackagingOpsStore?.packagingRequestBody?.inventory_id || 0;
  const [productPopupOpen, setProductPopupOpen] = useState(false);
  const [editPackPopupOpen, setEditPackPopupOpen] = useState(false);
  const [createInventoryPopup, setCreateInventoryPopup] = useState(false);
  const [completeOrder, setCompleteOrder] = useState(false);
  const [popupMode, setPopupMode] = useState<"pack" | "bulk">("pack");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSwagpackId, setActiveSwagpackId] = useState<number | null>(null);
  const [filteredProducts, setFilteredProducts] = useState<ProductData[]>([]);
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<HTMLElement | null>(null);

  useEffect(() => {
    setIsLoading(true);
    const fetchInitialData = async () => {
      await Promise.all([
        projectPackagingOpsStore?.getProjectPackagingOpsDetails(projectId),
        projectPackagingOpsStore?.getSourcingSwagpacks(projectId),
      ]).finally(() => {
        setIsLoading(false);
      });

      const inventoryId =
        projectPackagingOpsStore?.packagingRequestBody?.inventory_id;
      if (inventoryId) {
        projectPackagingOpsStore?.getPackagingOpsSwagpackImageVideo(
          projectId,
          inventoryId,
        );
      }
    };

    fetchInitialData()
      .catch((error) => {
        showError(
          "Error occurred",
          error?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (
      projectPackagingOpsStore?.packagingRequestBody?.products &&
      projectPackagingOpsStore?.packagingRequestBody?.products?.length > 0 &&
      projectPackagingOpsStore?.packagingSourcingSwagpacks &&
      projectPackagingOpsStore?.packagingSourcingSwagpacks?.length > 0
    ) {
      const result = projectPackagingOpsStore?.filterProductsForPopup();
      setFilteredProducts(result?.filter((item) => item != null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectPackagingOpsStore?.packagingRequestBody?.products,
    projectPackagingOpsStore?.packagingSourcingSwagpacks,
  ]);

  const handleDeletePack = (inventory_id: number, swagpack_id: number) => {
    const pack =
      projectPackagingOpsStore?.packagingRequestBody?.swagpacks?.find(
        (p) => p.swagpack_id === swagpack_id,
      );
    if (pack) {
      projectPackagingOpsStore?.setPackToDelete(pack);
      projectPackagingOpsStore?.setRemovePackPopupOpen(true);
    }
  };

  const handleRemovePackQuantity = useCallback(
    async (packToDelete: PackToDelete) => {
      const {
        inventory_id,
        swagpack_id,
        pack_remove_quantity,
        bulk_remove_quantity,
      } = packToDelete;

      const pack =
        projectPackagingOpsStore?.packagingRequestBody?.swagpacks?.find(
          (p) => p.swagpack_id === swagpack_id,
        );

      if (!pack) {
        showError("Error", "Pack not found");
        return;
      }

      const isBulkCase =
        pack.products?.length === 1 && pack.products?.[0]?.variants?.length > 0;

      if (isBulkCase) {
        if (!bulk_remove_quantity || bulk_remove_quantity?.length === 0) {
          showError(
            "Validation Error",
            "Please select at least one variant that has a quantity greater than zero",
          );
          return;
        }

        const variants = pack.products[0].variants || [];
        for (const bulkVariant of bulk_remove_quantity) {
          const availableVariant = variants.find(
            (v) => v.id === bulkVariant.id,
          );

          if (!availableVariant) {
            showError(
              "Validation Error",
              `Variant with size ${bulkVariant.size} not found`,
            );
            return;
          }

          if (bulkVariant.quantity <= 0) {
            showError(
              "Validation Error",
              `Remove quantity for size ${bulkVariant.size} must be greater than 0`,
            );
            return;
          }

          if (bulkVariant.quantity > availableVariant.quantity) {
            showError(
              "Validation Error",
              `Remove quantity for size ${bulkVariant.size} (${bulkVariant.quantity}) cannot exceed available quantity (${availableVariant.quantity})`,
            );
            return;
          }
        }
      } else {
        if (!pack_remove_quantity || pack_remove_quantity <= 0) {
          showError(
            "Validation Error",
            "Remove quantity must be greater than 0",
          );
          return;
        }

        if (pack_remove_quantity > pack.quantity) {
          showError(
            "Validation Error",
            `Remove quantity (${pack_remove_quantity}) cannot exceed available quantity (${pack.quantity})`,
          );
          return;
        }
      }

      setIsLoading(true);
      projectPackagingOpsStore?.setRemovePackPopupOpen(false);

      const payload: {
        pack_remove_quantity?: number;
        bulk_remove_quantity?: any[];
      } = {};

      if (pack_remove_quantity !== undefined) {
        payload.pack_remove_quantity = pack_remove_quantity;
      }

      if (bulk_remove_quantity && bulk_remove_quantity?.length > 0) {
        payload.bulk_remove_quantity = bulk_remove_quantity;
      }

      projectPackagingOpsStore
        ?.deletePackagingOpsSwagpack(inventory_id, swagpack_id, payload)
        .then((res) => {
          if (res) {
            showSuccess("Pack quantity removed successfully!");
            projectPackagingOpsStore?.getProjectPackagingOpsDetails(projectId);
          }
        })
        .catch((error) => {
          showError(
            "Error occurred",
            error?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [projectPackagingOpsStore, projectId, showError, showSuccess],
  );

  const handleCancelProduct = useCallback(() => setProductPopupOpen(false), []);
  const handleClearAll = useCallback(() => {}, []);
  const handlePackConfirm = useCallback(
    (selectedProducts: number[]) => {
      const queryParams = new URLSearchParams();
      queryParams.append("project-id", String(projectId));
      if (selectedProducts?.length) {
        queryParams.append("productId", selectedProducts?.join(","));
      }
      navigate(`/projects/create-pack?${queryParams?.toString()}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, navigate],
  );

  const handleAddClick = useCallback(
    (swagpack: PackagingSwagpack) => {
      setIsLoading(true);

      Promise.resolve()
        .then(() => {
          const swagpackProductIds = swagpack?.products?.map(
            (product: SwagpackProduct) => product?.product_id,
          );
          const queryParams = new URLSearchParams();
          queryParams.append("project-id", String(projectId));
          queryParams.append("productId", swagpackProductIds?.join(",") || "");
          queryParams.append("swagpack_id", String(swagpack?.swagpack_id));

          navigate(`/projects/create-pack?${queryParams?.toString()}`);
        })
        .catch((error) => {
          showError(
            "Error occurred",
            error?.response?.data?.message ||
              "Failed to check stock availability. Please try again.",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectPackagingOpsStore, projectId, navigate, showError],
  );

  const handleOpenProductPopup = useCallback(
    (mode: "pack" | "bulk") => {
      if (
        projectPackagingOpsStore?.packagingRequestBody?.products &&
        projectPackagingOpsStore?.packagingRequestBody?.products?.length > 0 &&
        projectPackagingOpsStore?.packagingSourcingSwagpacks &&
        projectPackagingOpsStore?.packagingSourcingSwagpacks?.length > 0
      ) {
        const result = projectPackagingOpsStore?.filterProductsForPopup();
        setFilteredProducts(result?.filter((item) => item != null));
        setPopupMode(mode);
        setProductPopupOpen(true);
        return;
      }
      if (!isLoading) {
        setIsLoading(true);
        Promise.all([
          projectPackagingOpsStore?.getProjectPackagingOpsDetails(projectId),
          projectPackagingOpsStore?.getSourcingSwagpacks(projectId),
        ])
          .then(() => {
            // Now get filtered products
            const result = projectPackagingOpsStore?.filterProductsForPopup();
            setFilteredProducts(result?.filter((item) => item != null));
            setPopupMode(mode);
            setProductPopupOpen(true);
          })
          .catch((error: any) => {
            showError(
              "Error occurred",
              error?.response?.data?.message ||
                "Failed to load products for selection. Please try again.",
            );
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    [projectPackagingOpsStore, projectId, showError, isLoading],
  );

  const handlePencilClick = useCallback(
    (swagpackId: number) => {
      const swagpack =
        projectPackagingOpsStore?.packagingRequestBody?.swagpacks?.find(
          (pack) => pack?.swagpack_id === swagpackId,
        );
      setActiveSwagpackId(swagpackId);
      setPopupMode(swagpack?.products?.length === 1 ? "bulk" : "pack");
      setEditPackPopupOpen(true);
    },
    [projectPackagingOpsStore?.packagingRequestBody?.swagpacks],
  );

  const handleCompleteProject = () => {
    setIsLoading(true);
    projectPackagingOpsStore
      ?.completeProject(projectId)
      .then((res) => {
        // The API returns the full response, check if it was successful
        if (res && res?.success) {
          showSuccess("Project completed successfully!");
        } else {
          showError(
            "Error occurred",
            "Failed to complete project. Please try again.",
          );
        }
      })
      .catch((error) =>
        showError(
          "Error occurred",
          error?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => setIsLoading(false));
  };

  const handleActionsMenuOpen = () => {
    if (!actionsAnchorRef.current) return;
    setActionsMenuAnchor(actionsAnchorRef.current);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (actionId: string) => {
    if (actionId === "create_inventory") {
      setCreateInventoryPopup(true);
    } else if (actionId === "media_gallery") {
      if (inventory_id) {
        navigate(
          `/projects/packaging-ops-media?image&project-id=${projectId}&inventory-id=${inventory_id}`,
        );
      } else {
        showError("Error", "Please create or map inventory first");
      }
    } else if (actionId === "complete") {
      setCompleteOrder(true);
    }
    handleActionsMenuClose();
  };

  const handleBackfillToggle = async (backfillAllowed: boolean) => {
    try {
      await projectManagementStore?.updateBackfillAllowed(
        projectId,
        backfillAllowed,
      );
      await projectPackagingOpsStore.getSourcingSwagpacks(projectId);
    } catch (error) {
      showError(
        "Error",
        (error as any)?.response?.data?.message ||
          "Failed to update backfill allowed",
      );
    }
  };

  const [packForImageUpload, setPackForImageUpload] =
    useState<PackagingSwagpack | null>(null);

  const uploadMedia = async (
    files: File[],
    type: "image",
    categoryOrContainer: string,
  ): Promise<string[]> => {
    if (files?.length === 0) return [];
    if (type === "image") {
      const imageAPI = new ImageAPI();
      return imageAPI?.uploadImage(
        files,
        categoryOrContainer || "product-images",
      );
    }
    return [];
  };
  const handleUpload = async (files: File[], packId: number) => {
    setIsModalLoading(true);
    uploadMedia(files, "image", "order-videos")
      .then((uploadedUrls) => {
        if (uploadedUrls?.length > 0 && projectId) {
          return projectPackagingOpsStore?.uploadPackagingPackImage(
            packId,
            Number(inventory_id),
            uploadedUrls?.[0],
          );
        }
      })
      .then(() => {
        if (projectId) {
          return projectPackagingOpsStore?.getProjectPackagingOpsDetails(
            projectId,
          );
        }
      })
      .then(() => {
        if (packForImageUpload && packForImageUpload?.swagpack_id === packId) {
          const updatedPack =
            projectPackagingOpsStore?.packagingRequestBody?.swagpacks?.find(
              (pack) => pack?.swagpack_id === packId,
            );
          if (updatedPack) {
            setPackForImageUpload(updatedPack);
          }
        }
        showSuccess("Image uploaded successfully!");
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload media";
        showError("Upload Error", errorMessage);
      })
      .finally(() => {
        setIsModalLoading(false);
      });
  };

  const renderActionsMenu = () => (
    <Menu
      anchorEl={actionsMenuAnchor}
      open={Boolean(actionsMenuAnchor)}
      onClose={handleActionsMenuClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      disableAutoFocusItem
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            boxShadow:
              "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
            minWidth: 240,
            overflow: "hidden",
          },
        },
      }}
    >
      {!projectPackagingOpsStore?.packagingRequestBody?.inventory_id && (
        <MenuItem
          onClick={() => handleActionSelect("create_inventory")}
          sx={{
            fontSize: "0.9rem",
            px: 2,
            py: 1.25,
            gap: 1,
            "&:hover": {
              backgroundColor:
                theme.palette.customColors?.lightPurple ||
                theme.palette.action.hover,
            },
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Create / Map Inventory
          </CustomTypography>
        </MenuItem>
      )}
      <MenuItem
        onClick={() => handleActionSelect("media_gallery")}
        sx={{
          fontSize: "0.9rem",
          px: 2,
          py: 1.25,
          gap: 1,
          borderTop: !projectPackagingOpsStore?.packagingRequestBody
            ?.inventory_id
            ? "1px solid " + theme.palette.divider
            : "none",
          "&:hover": {
            backgroundColor:
              theme.palette.customColors?.lightPurple ||
              theme.palette.action.hover,
          },
        }}
      >
        <CustomTypography variant="body2" fontWeight={600}>
          Media Gallery
        </CustomTypography>
      </MenuItem>
      <MenuItem
        onClick={() => handleActionSelect("complete")}
        disabled={
          !!(
            projectPackagingOpsStore?.packagingRequestBody?.inventory_id &&
            projectPackagingOpsStore?.packagingRequestBody?.archived === 1
          )
        }
        sx={{
          fontSize: "0.9rem",
          px: 2,
          py: 1.25,
          gap: 1,
          borderTop: "1px solid " + theme.palette.divider,
          "&:hover": {
            backgroundColor:
              theme.palette.customColors?.lightPurple ||
              theme.palette.action.hover,
          },
          "&.Mui-disabled": {
            opacity: 0.4,
          },
        }}
      >
        <CustomTypography variant="body2" fontWeight={600}>
          Complete
        </CustomTypography>
      </MenuItem>
    </Menu>
  );

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {isLoading ? (
        <Backdrop
          sx={(theme) => ({
            color: theme.palette.customColors.white,
            zIndex: theme.zIndex.drawer + 1,
          })}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <>
          {userStore?.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: { xs: "wrap", md: "nowrap" },
                }}
              >
                <CustomerHeading
                  label={
                    projectPackagingOpsStore?.packagingRequestBody?.label || ""
                  }
                  orderId={
                    projectPackagingOpsStore?.packagingRequestBody
                      ?.project_id || 0
                  }
                  moduleName="Packaging Ops"
                  brandName={
                    (projectPackagingOpsStore?.packagingRequestBody
                      ?.companyName || "") +
                    " | Label: " +
                    (projectPackagingOpsStore?.packagingRequestBody
                      ?.inventory_label || "") +
                    " | Inventory: " +
                    (projectPackagingOpsStore?.packagingRequestBody
                      ?.inventory_id !== undefined
                      ? Boolean(
                          projectPackagingOpsStore?.packagingRequestBody
                            ?.is_master,
                        )
                        ? `#${projectPackagingOpsStore.packagingRequestBody.inventory_id}` +
                          " " +
                          "Master"
                        : `#${projectPackagingOpsStore.packagingRequestBody.inventory_id}` +
                          " " +
                          "(Order Lvl)"
                      : "")
                  }
                />
                <Box
                  sx={{
                    color: theme.palette.customColors.greenDark,
                  }}
                >
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        justifyContent: {
                          xs: "flex-start",
                          sm: "flex-start",
                          md: "center",
                          lg: "center",
                        },
                        marginTop: { md: "2rem" },
                        flexWrap: {
                          md: "wrap",
                          lg: "nowrap",
                          sm: "wrap",
                          xs: "wrap",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "180px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          border: "1px solid #86898F",
                          borderRadius: "8px",
                          height: "40px",
                          paddingLeft: "10px",
                        }}
                      >
                        <CustomTypography
                          variant="body2"
                          fontWeight={600}
                          color={theme.palette.customColors?.text}
                        >
                          Backfill Allowed
                        </CustomTypography>
                        <CustomToggleSwitch
                          checked={
                            projectManagementStore?.currentProjectDetails
                              ?.backfill_allowed || false
                          }
                          onChange={(e) => {
                            handleBackfillToggle(e.target.checked);
                          }}
                          activeColor={theme.palette.customColors?.purple}
                          inactiveColor={
                            theme.palette.customColors?.switchTrackGray ||
                            "#ccc"
                          }
                          disabled={
                            userStore?.hasPermission(
                              EmployeeRoles.MARK_ORDER_AS_BACKFILL_ALLOWED,
                            ) !== true
                          }
                        />
                      </Box>
                      <Box ref={actionsAnchorRef}>
                        <AppButton
                          label="Actions"
                          onClick={handleActionsMenuOpen}
                          customStyles={{
                            height: "38px",
                            minWidth: "140px",
                          }}
                          variant="grayscale"
                        />
                      </Box>
                      {renderActionsMenu()}
                    </Box>

                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        gap: 2,
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                        flexWrap: {
                          md: "wrap",
                          lg: "nowrap",
                          sm: "wrap",
                          xs: "wrap",
                        },
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color={theme.palette.customColors?.text}
                        fontWeight={600}
                      >
                        Backfill status updated by{" "}
                        {projectManagementStore.currentProjectDetails
                          .backfill_allowed_by_name || ""}{" "}
                        on{" "}
                        {formatDate(
                          projectManagementStore.currentProjectDetails
                            .backfill_allowed_date || "",
                        )}
                      </CustomTypography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mt: 2, mb: 3 }} />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  flexWrap: "wrap",
                  width: "100%",
                  paddingBottom: "1rem",
                  opacity: projectPackagingOpsStore?.packagingRequestBody
                    ?.inventory_id
                    ? 1
                    : 0.5,
                  pointerEvents: projectPackagingOpsStore?.packagingRequestBody
                    ?.inventory_id
                    ? "auto"
                    : "none",
                }}
              >
                <Box
                  sx={{
                    flex: "1 1 auto",
                    minWidth: { xs: "100%", md: "200px" },
                  }}
                >
                  <CustomTextField
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(
                        e?.target?.value?.replace(/\s+/g, " ")?.trim(),
                      )
                    }
                    width="100%"
                    placeholder="Search by Swagpack Name"
                    customStyles={{
                      backgroundColor:
                        theme?.palette?.customColors?.grayBackGroundColor,
                    }}
                  />
                </Box>
                <Box gap={2} display="flex" flexWrap="wrap">
                  {userStore?.hasPermission(EmployeeRoles.CREATE_SWAGPACK) && (
                    <AppButton
                      label="Create Pack"
                      customStyles={{
                        height: "38px",
                        minWidth: "9.438rem",
                        color: theme?.palette?.customColors?.blackBorder,
                      }}
                      startIcon={<AddIcon />}
                      variant="grayscale"
                      onClick={() => handleOpenProductPopup("pack")}
                      disabled={
                        projectPackagingOpsStore?.packagingRequestBody
                          ?.archived === 1
                      }
                    />
                  )}

                  {userStore?.hasPermission(EmployeeRoles.CREATE_SWAGPACK) && (
                    <AppButton
                      label="Create Bulk"
                      customStyles={{
                        height: "38px",
                        minWidth: "9.438rem",
                        backgroundColor: theme?.palette?.customColors?.purple,
                        color: theme?.palette?.customColors?.white,
                      }}
                      variant="primary"
                      startIcon={CreateBulkIcon}
                      onClick={() => handleOpenProductPopup("bulk")}
                      disabled={
                        projectPackagingOpsStore?.packagingRequestBody
                          ?.archived === 1
                      }
                    />
                  )}
                </Box>
              </Box>

              <Box>
                <PackagingOpsTable
                  handleNavigateById={() => {}}
                  archived={
                    projectPackagingOpsStore?.packagingRequestBody?.archived
                  }
                  handleImageUpload={(pack: PackagingSwagpack) => {
                    setPackForImageUpload(pack);
                  }}
                  swagpacks={
                    projectPackagingOpsStore?.packagingRequestBody?.swagpacks?.filter(
                      (pack) =>
                        pack?.swagpack_name
                          ?.toLowerCase()
                          ?.includes(
                            searchTerm
                              ?.toLowerCase()
                              ?.replace(/\s+/g, " ")
                              ?.trim(),
                          ),
                    ) || []
                  }
                  handlePencilClick={handlePencilClick}
                  handleDeletePack={(
                    inventory_id: number,
                    swagpack_id: number,
                  ) => {
                    handleDeletePack(inventory_id, swagpack_id);
                  }}
                  handleAddClick={handleAddClick}
                  isLoading={isLoading}
                  hasCreateSwagpackPermission={userStore?.hasPermission(
                    EmployeeRoles.CREATE_SWAGPACK,
                  )}
                />
                <Dialog
                  open={!!packForImageUpload}
                  onClose={() => {
                    setPackForImageUpload(null);
                  }}
                  maxWidth="md"
                  fullWidth
                  sx={{
                    "& .MuiDialog-paper": {
                      padding: 4,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <CustomTypography variant="heading4" fontWeight={700}>
                      {projectPackagingOpsStore?.packagingRequestBody?.swagpacks?.find(
                        (pack) =>
                          pack?.swagpack_id === packForImageUpload?.swagpack_id,
                      )?.swagpack_name || "Pack Images"}
                    </CustomTypography>
                    <IconButton
                      onClick={() => {
                        setPackForImageUpload(null);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, paddingBottom: 2 }}>
                    <Box
                      sx={{
                        flex: 1.5,
                      }}
                    >
                      <ImageCarousel
                        images={
                          packForImageUpload?.image_url ||
                          packForImageUpload?.pack_image_url
                            ? [
                                {
                                  image_url:
                                    packForImageUpload?.image_url ||
                                    packForImageUpload?.pack_image_url ||
                                    "",
                                },
                              ]
                            : []
                        }
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      {isModalLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "50%",
                            gap: 2,
                          }}
                        >
                          <CircularProgress size={40} />
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                          >
                            Uploading image...
                          </CustomTypography>
                        </Box>
                      ) : (
                        <ImageUpload
                          size="large"
                          showImagesList={false}
                          uploadText="Drop your files here, or browse"
                          addImagesToBeUploaded={(files: File[]) =>
                            packForImageUpload &&
                            handleUpload(files, packForImageUpload.swagpack_id!)
                          }
                          disabled={isModalLoading}
                        />
                      )}
                    </Box>
                  </Box>
                </Dialog>
              </Box>
              <SelectProductPopup
                open={productPopupOpen}
                mode={popupMode}
                products={
                  Array.isArray(filteredProducts)
                    ? filteredProducts?.map((productData) => {
                        const availableQuantity =
                          productData?.availableQuantity || 0;

                        return {
                          ...productData,
                          image_url: productData?.image_url || "",
                          product_name: productData?.name || "",
                          product_id: String(productData?.productId || ""),
                          quantity: availableQuantity,
                        };
                      })
                    : []
                }
                selectedProductIds={[]}
                onClose={handleCancelProduct}
                onConfirm={handlePackConfirm}
                onClearAll={handleClearAll}
              />

              <EditPackPopup
                mode={popupMode}
                open={editPackPopupOpen}
                Swagpacks={
                  projectPackagingOpsStore?.packagingRequestBody?.swagpacks ||
                  []
                }
                activeSwagpackId={activeSwagpackId}
                onClose={() => setEditPackPopupOpen(false)}
              />
              {completeOrder && (
                <ConfirmationPopup
                  open={completeOrder}
                  id={
                    projectPackagingOpsStore?.packagingRequestBody
                      ?.project_id || 0
                  }
                  title="Confirm Completion"
                  content="Are you sure you want to complete this order ?"
                  onConfirm={() => {
                    handleCompleteProject();
                    setCompleteOrder(false);
                  }}
                  onCancel={() => setCompleteOrder(false)}
                />
              )}

              {completeOrder && (
                <ConfirmationPopup
                  open={completeOrder}
                  id={
                    projectPackagingOpsStore?.packagingRequestBody
                      ?.project_id || 0
                  }
                  title="Confirm Completion"
                  content="Are you sure you want to complete this order ?"
                  onConfirm={() => {
                    handleCompleteProject();
                    setCompleteOrder(false);
                  }}
                  onCancel={() => setCompleteOrder(false)}
                />
              )}

              <RemovePackPopup
                open={projectPackagingOpsStore?.removePackPopupOpen}
                pack={
                  projectPackagingOpsStore?.packToDelete as PackagingSwagpack
                }
                onConfirm={handleRemovePackQuantity}
                onCancel={() => {
                  projectPackagingOpsStore?.setRemovePackPopupOpen(false);
                  projectPackagingOpsStore?.setPackToDelete(null);
                }}
              />
              <CreateInventoryPopup
                open={createInventoryPopup}
                onClose={() => setCreateInventoryPopup(false)}
                orderId={projectId}
                customerId={
                  projectPackagingOpsStore?.packagingRequestBody?.customerId ??
                  0
                }
                labelInputText="Enter Inventory Label"
                onCreate={({ customerId, label, orderId, type }) => {
                  setIsLoading(true);
                  projectPackagingOpsStore
                    ?.createInventory(customerId, label, orderId, type)
                    .then(async () => {
                      showSuccess("Inventory created successfully!");
                      await Promise.all([
                        projectPackagingOpsStore?.getProjectPackagingOpsDetails(
                          projectId,
                        ),
                        projectPackagingOpsStore?.getSourcingSwagpacks(
                          projectId,
                        ),
                      ]);
                    })
                    .catch((error) => {
                      showError(
                        "Error occurred",
                        error?.response?.data?.message ||
                          "Something went wrong! Please try again later.",
                      );
                    })
                    .finally(() => {
                      setIsLoading(false);
                      setCreateInventoryPopup(false);
                    });
                }}
              />
            </Box>
          ) : (
            <PermissionBlockLabel />
          )}
        </>
      )}
    </Grid2>
  );
};
export default observer(PackagingOps);
